import { useEffect } from 'react'
import {
  type AreaData,
  DAY,
  isNotDefined,
  Logger,
  MINUTE,
  type AreaListInput,
  type AreaListSuccess,
  type LatLngBoundingBox,
} from '@terros/common'
import { keepPreviousData } from '@tanstack/react-query'
import { useTantalimQuery } from '../query'
import { areaListInfiniteKey, areaCanvassKey } from '../keys'
import { useTantalimInfiniteQuery, type GetInfiniteHookResponse } from '../infiniteQuery'
import { type GetHookResponse } from '../hooks'

const logger = new Logger('useAreas')

const AREA_LIST_V2_SIZE = 100
const AREA_LIST_ALL_PAGES_SIZE = 1000

export function useInfiniteAreas(
  input: AreaListInput = {},
  enabled = true,
  options: { keepPreviousData?: boolean } = {}
): GetInfiniteHookResponse<AreaData> {
  const areaListInput: AreaListInput = { ...input, version: 2, size: input.size ?? AREA_LIST_V2_SIZE }
  return useTantalimInfiniteQuery<AreaListInput, AreaListSuccess, AreaData>({
    url: '/area/list',
    queryKey: areaListInfiniteKey(areaListInput),
    input: areaListInput,
    selectRecords: (page) => page.areas,
    placeholderData: options.keepPreviousData ? keepPreviousData : undefined,
    options: {
      gcTime: DAY,
      staleTime: 15 * MINUTE,
      enabled,
    },
  })
}

export function useAllAreas(input: AreaListInput = {}, enabled = true): GetInfiniteHookResponse<AreaData> {
  const pageSize = input.size ?? AREA_LIST_ALL_PAGES_SIZE
  const result = useInfiniteAreas({ ...input, size: pageSize }, enabled)
  const shouldFetchNext = enabled && shouldFetchNextAreaPage(result, pageSize)

  const { fetchNextPage } = result

  useEffect(() => {
    if (!shouldFetchNext) return
    fetchNextPage?.()
  }, [shouldFetchNext, fetchNextPage])

  return result
}

/** A map draws no more polygons than this, so the viewport query never pages past the first. */
const AREA_BOUNDS_SIZE = 1000

/** Areas inside a map viewport. Keeps the last viewport's areas on screen while the next one loads. */
export function useAreasInBounds(boundingBox: LatLngBoundingBox, enabled = true): GetInfiniteHookResponse<AreaData> {
  const result = useInfiniteAreas({ boundingBox, size: AREA_BOUNDS_SIZE }, enabled, { keepPreviousData: true })
  const { total, data } = result

  useEffect(() => {
    if (isNotDefined(total) || total <= data.length) return
    logger.warn(`Map bounds hold ${total} areas, showing the first ${data.length}`)
  }, [total, data.length])

  return result
}

type AllAreasProgress = Pick<
  GetInfiniteHookResponse<AreaData>,
  'data' | 'total' | 'isLoading' | 'error' | 'hasNextPage' | 'isFetchingNextPage'
>

/** Backstop against a cursor that never ends: useAllAreas stops after this many pages. */
export const AREA_LIST_MAX_PAGES = 100

/** True when useAllAreas should request the next page: the cursor has more and the page guard is not hit. */
export function shouldFetchNextAreaPage(result: AllAreasProgress, pageSize = AREA_LIST_ALL_PAGES_SIZE): boolean {
  if (result.hasNextPage !== true) return false
  if (result.isFetchingNextPage === true) return false
  return result.data.length < pageSize * AREA_LIST_MAX_PAGES
}

/** True until useAllAreas has drained every page. A failed page ends the wait so callers can show the error. */
export function isLoadingAllAreas(result: AllAreasProgress, pageSize = AREA_LIST_ALL_PAGES_SIZE): boolean {
  if (result.isLoading) return true
  if (result.error) return false
  return result.isFetchingNextPage === true || shouldFetchNextAreaPage(result, pageSize)
}

export function useMyAreas(enabled = true): GetHookResponse<AreaData[]> {
  return useTantalimQuery<AreaListInput, AreaListSuccess, AreaData[]>({
    url: '/area/canvass',
    queryKey: areaCanvassKey(),
    input: {},
    convertResponse: (data) => data.areas,
    options: {
      gcTime: DAY,
      staleTime: 15 * MINUTE,
      enabled,
    },
  })
}
